import { useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useHistory } from "react-router-dom";
import { TbArrowLeft, TbCheck, TbChevronRight } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useToast } from "../../../../Hooks/toast";
import { formatBaasCreateConflictMessage } from "../../../../Services/baasCreateConflictMessage";
import {
  buildBaasProgramCode,
  normalizeBaasProgramCode,
  fetchBaasInstanceDetail,
  isBaasKybPending,
  resolveBaasCreateParentInstance,
  setInstanceEfxFees,
  setInstancePixFees,
  setInstanceStablesFees,
} from "../../../../Services/baasInstancesApi";
import { fetchBaasInstanceScopes } from "../../../../Services/baasApiApi";
import { loadBaasExitRailRootAccountSelectOptions } from "./baasRootAccountOptions";
import { loadBaasOwnerEntitySelectOptions } from "./baasOwnerEntityOptions";
import { BAAS_BASE_PATH, BAAS_INSTANCE_CREATE_PATH } from "../constants";
import { BaasInstancesSkeleton } from "../components/BaasSkeleton";
import {
  BAAS_INSTANCE_FEATURE_OPTIONS,
  BAAS_PUBLISH_SCOPE_CATALOG,
  BAAS_SUPPORTED_COUNTRY_OPTIONS,
  buildDefaultEfxFeeDrafts,
  buildDefaultEfxLimitDrafts,
  buildDefaultFeatureMap,
  buildDefaultNetworkFeeDrafts,
  buildDefaultPixFeeDrafts,
  buildDefaultPixLimitDrafts,
  buildDefaultStablesFeeDrafts,
  buildDefaultStablesLimitDrafts,
  buildWizardSteps,
  efxFeePoliciesFromDrafts,
  efxLimitPoliciesFromDrafts,
  hasInstancesOwnerSetScope,
  isGrantableScope,
  mergeScopeCatalog,
  networkFeePoliciesFromDrafts,
  pixFeePoliciesFromDrafts,
  pixLimitPoliciesFromDrafts,
  resolveCountriesPayload,
  resolveMatrixRootAccountId,
  stablesFeePoliciesFromDrafts,
  stablesLimitPoliciesFromDrafts,
  withCardsCoreScopes,
} from "./baasInstanceCreateConstants";
import { BaasInstanceScopesPicker } from "./components/BaasInstanceScopesPicker";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const toggleTrackClass = (on) =>
  `relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition ${
    on ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-gray-700"
  }`;

const toggleThumbClass = (on) =>
  `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
    on ? "translate-x-5" : "translate-x-0"
  }`;

const panelClass =
  "space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface sm:p-5";

const ROOT_CUSTOM = "__custom__";
const ROOT_VIRTUAL = "__virtual__";

const emptySmtp = () => ({
  host: "smtp.sendgrid.net",
  port: "587",
  username: "apikey",
  password: "",
  fromEmail: "",
  fromName: "",
});

const ToggleRow = ({ label, hint, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-brand-dark-border">
    <div>
      <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{label}</p>
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`${toggleTrackClass(checked)} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span className={toggleThumbClass(checked)} />
    </button>
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2.5 last:border-0 dark:border-brand-dark-border sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <dt className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-sm text-brand-dark dark:text-gray-100 sm:text-right">{value}</dd>
  </div>
);

export const BaasInstanceCreatePage = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { instances, createInstance, loading: instancesLoading } = useBaasInstance();
  const {
    canCreateInstance,
    isValutConfigAdmin,
    loading: createAccessLoading,
    viewAsActive,
  } = useBaasPlatformCreateAccess();

  const [stepIndex, setStepIndex] = useState(0);
  const isProduction = false;
  const [ownerEntityId, setOwnerEntityId] = useState("");
  const [ownerEntityOptions, setOwnerEntityOptions] = useState([]);
  const [loadingOwnerEntities, setLoadingOwnerEntities] = useState(false);
  const [instanceName, setInstanceName] = useState("");
  const [definition, setDefinition] = useState("");
  const [supportedCountries, setSupportedCountries] = useState(["BR"]);
  const [allowKycApproval, setAllowKycApproval] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [allowedIps, setAllowedIps] = useState("");
  const [rootAccountChoice, setRootAccountChoice] = useState("");
  const [rootAccountCustom, setRootAccountCustom] = useState("");
  const [parentRootAccountId, setParentRootAccountId] = useState("");
  const [registerSmtp, setRegisterSmtp] = useState(false);
  const [smtp, setSmtp] = useState(emptySmtp);
  const [rootOptions, setRootOptions] = useState([]);
  const [loadingRoots, setLoadingRoots] = useState(false);
  const rootDefaultAppliedRef = useRef(false);
  const [scopeCatalog, setScopeCatalog] = useState(BAAS_PUBLISH_SCOPE_CATALOG);
  const [selectedScopes, setSelectedScopes] = useState(() => [...BAAS_PUBLISH_SCOPE_CATALOG]);
  const [parentRawScopes, setParentRawScopes] = useState([]);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [features, setFeatures] = useState(buildDefaultFeatureMap);
  const [pixDrafts, setPixDrafts] = useState(buildDefaultPixLimitDrafts);
  const [stablesDrafts, setStablesDrafts] = useState(buildDefaultStablesLimitDrafts);
  const [efxDrafts, setEfxDrafts] = useState(buildDefaultEfxLimitDrafts);
  const [pixFeeDrafts, setPixFeeDrafts] = useState(buildDefaultPixFeeDrafts);
  const [stablesFeeDrafts, setStablesFeeDrafts] = useState(buildDefaultStablesFeeDrafts);
  const [efxFeeDrafts, setEfxFeeDrafts] = useState(buildDefaultEfxFeeDrafts);
  const [networkFeeDrafts, setNetworkFeeDrafts] = useState(buildDefaultNetworkFeeDrafts);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [programCode, setProgramCode] = useState("");
  const [programCodeTouched, setProgramCodeTouched] = useState(false);
  /** Valut admin only: when true, send clone_from_instance_id; when false, send explicit scopes/features/limits. */
  const [cloneConfig, setCloneConfig] = useState(false);

  const programCodePreview = useMemo(() => {
    if (programCodeTouched) {
      return normalizeBaasProgramCode(programCode);
    }
    return buildBaasProgramCode(instanceName);
  }, [instanceName, programCode, programCodeTouched]);
  // Always create against prod/catalog parent; MobileService orchestrates cross-DB HML create.
  const parentInstance = useMemo(
    () => resolveBaasCreateParentInstance(instances, "production"),
    [instances],
  );
  const canCreate = Boolean(parentInstance) && !instancesLoading;

  /**
   * Matrix admins elevate to a key with instances.owner.set even when the create-parent
   * GET scopes omit non-grantable owner.set — require/send owner in that case too.
   * Closed during "Visualizar como" so elevated key scopes do not leak owner UI.
   */
  const requiresOwnerEntity = useMemo(
    () =>
      !viewAsActive &&
      (hasInstancesOwnerSetScope(parentRawScopes) || Boolean(isValutConfigAdmin)),
    [viewAsActive, parentRawScopes, isValutConfigAdmin],
  );

  /**
   * Valut INFRA (matrix / super_user) — same gate as Configuração tabs.
   * Must NOT use partner-key GET …/scopes (those omit instances.limits.write).
   */
  const isValutAdmin = Boolean(isValutConfigAdmin);

  /**
   * Liberate-at-create is disabled: is_production is always false on POST.
   * Valut liberates later via Aprovar produção (PATCH).
   */

  /** Conta root: only Valut global admin / instances.owner.set may change matrix exit rail. */
  const canEditRootAccount = useMemo(
    () =>
      !viewAsActive &&
      (hasInstancesOwnerSetScope(parentRawScopes) || Boolean(isValutConfigAdmin)),
    [viewAsActive, parentRawScopes, isValutConfigAdmin],
  );

  const steps = useMemo(() => buildWizardSteps(isValutAdmin), [isValutAdmin]);
  const currentStep = steps[stepIndex] ?? steps[steps.length - 1];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;

  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(Math.max(0, steps.length - 1));
    }
  }, [steps.length, stepIndex]);

  useEffect(() => {
    if (!parentInstance) {
      setRootOptions([]);
      setParentRootAccountId("");
      setScopeCatalog(BAAS_PUBLISH_SCOPE_CATALOG);
      setSelectedScopes([...BAAS_PUBLISH_SCOPE_CATALOG]);
      setParentRawScopes([]);
      rootDefaultAppliedRef.current = false;
      return undefined;
    }

    let cancelled = false;
    setLoadingRoots(true);
    setLoadingScopes(true);
    rootDefaultAppliedRef.current = false;

    loadBaasExitRailRootAccountSelectOptions(parentInstance)
      .then((options) => {
        if (!cancelled) setRootOptions(options);
      })
      .catch(() => {
        if (!cancelled) setRootOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRoots(false);
      });

    fetchBaasInstanceDetail(parentInstance)
      .then((detail) => {
        if (cancelled) return;
        setParentRootAccountId(String(detail?.rootAccountId || "").trim());
      })
      .catch(() => {
        if (!cancelled) {
          setParentRootAccountId(String(parentInstance?.rootAccountId || "").trim());
        }
      });

    fetchBaasInstanceScopes(parentInstance)
      .then((result) => {
        if (cancelled) return;
        const rawScopes = result?.scopes || [];
        setParentRawScopes(rawScopes);
        const parentScopes = rawScopes.filter(isGrantableScope);
        const catalog = mergeScopeCatalog(parentScopes);
        setScopeCatalog(catalog);
        setSelectedScopes(parentScopes.length > 0 ? parentScopes : [...BAAS_PUBLISH_SCOPE_CATALOG]);
      })
      .catch(() => {
        if (!cancelled) {
          setParentRawScopes([]);
          setScopeCatalog(BAAS_PUBLISH_SCOPE_CATALOG);
          setSelectedScopes([...BAAS_PUBLISH_SCOPE_CATALOG]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingScopes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [parentInstance]);

  useEffect(() => {
    if (!parentInstance || loadingRoots || loadingScopes || createAccessLoading) return;

    const parentForRoot = parentRootAccountId
      ? { ...parentInstance, rootAccountId: parentRootAccountId }
      : parentInstance;
    const defaultId = resolveMatrixRootAccountId(parentForRoot, rootOptions);
    if (!defaultId) return;

    const applyDefault = () => {
      if (rootOptions.some((option) => option.id === defaultId)) {
        setRootAccountChoice(defaultId);
        setRootAccountCustom("");
      } else {
        setRootAccountChoice(ROOT_CUSTOM);
        setRootAccountCustom(defaultId);
      }
    };

    if (!canEditRootAccount) {
      applyDefault();
      return;
    }

    if (rootDefaultAppliedRef.current) return;
    rootDefaultAppliedRef.current = true;
    applyDefault();
  }, [
    parentInstance,
    parentRootAccountId,
    rootOptions,
    loadingRoots,
    loadingScopes,
    createAccessLoading,
    canEditRootAccount,
  ]);

  useEffect(() => {
    if (!parentInstance || !requiresOwnerEntity) {
      setOwnerEntityOptions([]);
      return undefined;
    }

    let cancelled = false;
    setLoadingOwnerEntities(true);

    loadBaasOwnerEntitySelectOptions(parentInstance)
      .then((options) => {
        if (cancelled) return;
        setOwnerEntityOptions(Array.isArray(options) ? options : []);
      })
      .catch(() => {
        if (!cancelled) setOwnerEntityOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOwnerEntities(false);
      });

    return () => {
      cancelled = true;
    };
  }, [parentInstance, requiresOwnerEntity]);

  // Prefill owner from create-parent / clone source when the wizard has none selected.
  useEffect(() => {
    const fromParent = String(parentInstance?.ownerEntityId || "").trim();
    if (!fromParent) return;
    setOwnerEntityId((current) => (String(current || "").trim() ? current : fromParent));
  }, [parentInstance?.ownerEntityId]);

  const resolveRootAccountId = () => {
    if (rootAccountChoice === ROOT_VIRTUAL) return null;
    if (rootAccountChoice === ROOT_CUSTOM) {
      return rootAccountCustom.trim() || null;
    }
    return rootAccountChoice.trim() || null;
  };

  const useVirtualRoot = rootAccountChoice === ROOT_VIRTUAL;

  const isIssuerOrCardsPreset = Boolean(features.cards);

  const rootAccountLabel = useMemo(() => {
    if (rootAccountChoice === ROOT_VIRTUAL) {
      return intl.get("baas.instances.createPage.virtualRootLabel");
    }
    if (rootAccountChoice === ROOT_CUSTOM) {
      return rootAccountCustom.trim() || intl.get("baas.instances.createModal.rootAccountNone");
    }
    if (!rootAccountChoice) {
      return intl.get("baas.instances.createModal.rootAccountNone");
    }
    return rootOptions.find((option) => option.id === rootAccountChoice)?.label || rootAccountChoice;
  }, [rootAccountChoice, rootAccountCustom, rootOptions]);

  const enabledFeatures = useMemo(
    () => BAAS_INSTANCE_FEATURE_OPTIONS.filter((item) => features[item.code]),
    [features],
  );

  const countriesPayload = useMemo(
    () => resolveCountriesPayload(supportedCountries),
    [supportedCountries],
  );

  const handleScopePresetEnabled = (preset) => {
    if (preset?.syncFeature === "cards") {
      setFeatures((prev) => ({ ...prev, cards: true }));
      // Cards-only / emissor default: instance-owned virtual root (not Valut matrix).
      setRootAccountChoice(ROOT_VIRTUAL);
    }
  };

  const toggleCountry = (code) => {
    setSupportedCountries((current) => {
      if (current.includes(code)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== code);
      }
      return [...current, code];
    });
  };

  const showEfxLimits = Boolean(features.fiat);

  const validateStep = (stepId) => {
    if (stepId === "basics") {
      if (!instanceName.trim()) {
        setFormError(intl.get("baas.instances.createModal.nameLabel"));
        return false;
      }
      if (!canCreate) {
        setFormError(intl.get("baas.instances.createPage.errorParentRequired"));
        return false;
      }
      if (supportedCountries.length === 0) {
        setFormError(intl.get("baas.instances.createPage.countriesRequired"));
        return false;
      }
      if (requiresOwnerEntity && !String(ownerEntityId || "").trim()) {
        setFormError(intl.get("baas.instances.createPage.ownerEntityRequired"));
        return false;
      }
    }

    if (stepId === "root" && canEditRootAccount) {
      if (rootAccountChoice === ROOT_VIRTUAL) {
        return true;
      }
      const selected = rootOptions.find((option) => option.id === rootAccountChoice);
      if (selected?.role === "business") {
        setFormError(intl.get("baas.instances.createModal.rootAccountMustBePerson"));
        return false;
      }
    }

    if (stepId === "smtp" && registerSmtp) {
      if (!smtp.fromEmail.trim() || !smtp.password.trim()) {
        setFormError(intl.get("baas.instances.createModal.smtpRequired"));
        return false;
      }
    }

    setFormError(null);
    return true;
  };

  const goToStep = (index) => {
    if (submitting || index < 0 || index >= steps.length) return;
    if (index > stepIndex) {
      for (let i = stepIndex; i < index; i += 1) {
        if (!validateStep(steps[i].id)) {
          setStepIndex(i);
          return;
        }
      }
    }
    setFormError(null);
    setStepIndex(index);
  };

  const handleNext = () => {
    if (submitting || !validateStep(currentStep.id)) return;
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (submitting) return;
    if (isFirstStep) {
      history.push(BAAS_BASE_PATH);
      return;
    }
    setFormError(null);
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isLastStep) {
      handleNext();
      return;
    }

    if (submitting) return;

    const gateSteps = isValutAdmin
      ? ["basics", "smtp"]
      : ["basics", "smtp"];
    for (const stepId of gateSteps) {
      if (!validateStep(stepId)) {
        const index = steps.findIndex((step) => step.id === stepId);
        if (index >= 0) setStepIndex(index);
        return;
      }
    }

    setSubmitting(true);
    setFormError(null);
    try {
      if (!programCodePreview) {
        setFormError(intl.get("baas.instances.createPage.programCodeHint"));
        setSubmitting(false);
        return;
      }

      const selectedOwner = String(ownerEntityId || "").trim();
      const cloneSourceOwner = String(parentInstance?.ownerEntityId || "").trim();
      // Always send owner when known (picker or clone/parent source) so elevated matrix
      // keys with instances.owner.set do not 422.
      const resolvedOwnerEntityId = selectedOwner || cloneSourceOwner || undefined;
      const payload = {
        name: instanceName.trim(),
        environment: "production",
        isProduction: false,
        ownerEntityId: resolvedOwnerEntityId,
        programCode: programCodePreview,
        description: definition.trim() || null,
        countryCode: countriesPayload.countryCode,
        currencies: countriesPayload.currencies,
        allowSkipKyc: allowKycApproval,
        receiveEmail: false,
        templateId: templateId.trim() || null,
        allowedIps: allowedIps.trim() || null,
        rootAccountId: useVirtualRoot ? null : resolveRootAccountId(),
        useVirtualRoot: useVirtualRoot || undefined,
        registerSmtp,
        smtp: registerSmtp
          ? {
              host: smtp.host.trim() || undefined,
              port: smtp.port.trim() ? Number(smtp.port) : undefined,
              username: smtp.username.trim() || undefined,
              password: smtp.password.trim() || undefined,
              fromEmail: smtp.fromEmail.trim() || undefined,
              fromName: smtp.fromName.trim() || undefined,
            }
          : null,
        features: BAAS_INSTANCE_FEATURE_OPTIONS.map((item) => ({
          feature_code: item.code,
          enabled: Boolean(features[item.code]),
        })),
      };

      // Clients omit scopes + limits + features → Baas clones from parent / DefaultPartnerScopes.
      // Valut admin: optional clone toggle; when off, send explicit selections (no clone_from).
      if (isValutAdmin) {
        if (cloneConfig) {
          payload.cloneFromInstanceId = Number(parentInstance?.baasInstanceId) || undefined;
          delete payload.features;
        } else {
          payload.scopes = selectedScopes;
          payload.pixLimits = pixLimitPoliciesFromDrafts(pixDrafts);
          payload.stablesLimits = stablesLimitPoliciesFromDrafts(stablesDrafts);
          payload.efxLimits = showEfxLimits ? efxLimitPoliciesFromDrafts(efxDrafts) : [];
        }
      } else {
        delete payload.features;
        payload.cloneFromInstanceId = Number(parentInstance?.baasInstanceId) || undefined;
      }

      const created = await createInstance(payload);

      if (isValutAdmin && !cloneConfig && created) {
        try {
          await Promise.all([
            setInstancePixFees(created, pixFeePoliciesFromDrafts(pixFeeDrafts)),
            showEfxLimits
              ? setInstanceEfxFees(created, efxFeePoliciesFromDrafts(efxFeeDrafts))
              : Promise.resolve(),
            setInstanceStablesFees(created, {
              policies: stablesFeePoliciesFromDrafts(stablesFeeDrafts),
              networkFees: networkFeePoliciesFromDrafts(networkFeeDrafts),
            }),
          ]);
        } catch {
          addToast({
            type: "warning",
            title: intl.get("baas.instances.createPage.feesAfterCreateWarning"),
          });
        }
      }

      addToast({
        type: "success",
        title: intl.get("baas.instances.createPage.success"),
      });

      if (isBaasKybPending(created)) {
        history.push("/baas/kyb");
      } else {
        history.push(BAAS_BASE_PATH);
      }
    } catch (error) {
      const code = error?.code || error?.data?.error;
      let message = error?.detail || error?.message || intl.get("baas.instances.createPage.errorGeneric");

      if (code === "instance_create_parent_required") {
        message = intl.get("baas.instances.createPage.errorParentRequired");
      } else if (
        code === "instance_create_conflict" ||
        code === "entity_already_exists" ||
        code === "duplicate_request" ||
        error?.status === 409
      ) {
        message = formatBaasCreateConflictMessage(
          error,
          intl.get("baas.instances.createPage.errorConflict"),
        );
      } else if (
        error?.status === 403 ||
        code === "instance_create_forbidden" ||
        code === "forbidden"
      ) {
        const detail = String(error?.detail || error?.data?.detail || "").trim();
        message = detail || intl.get("baas.instances.createPage.errorForbidden");
      } else if (code === "hml_api_key_missing") {
        const detail = String(error?.detail || error?.data?.detail || "").trim();
        message = detail || intl.get("baas.instances.createPage.errorHmlApiKeyMissing");
      } else if (
        code === "hml_create_failed" ||
        code === "hml_create_invalid_response" ||
        code === "cross_db_create_not_configured" ||
        error?.status === 502 ||
        error?.status === 503
      ) {
        // MS dual-write failure — prod stays Active; detail explains missing HML twin.
        const detail = String(error?.detail || error?.data?.detail || "").trim();
        const prodId = error?.prod_instance_id ?? error?.data?.prod_instance_id;
        message =
          detail ||
          (prodId
            ? intl.get("baas.instances.createPage.errorHmlMissing", { id: prodId })
            : message);
      }

      setFormError(message);
      addToast({
        type: "error",
        title: intl.get("baas.instances.createPage.errorTitle"),
        description: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const yesNo = (value) =>
    value
      ? intl.get("baas.instances.createPage.reviewYes")
      : intl.get("baas.instances.createPage.reviewNo");

  const countriesReviewLabel =
    supportedCountries.length > 0
      ? supportedCountries
          .map((code) => intl.get(`baas.instances.createPage.country.${code}`))
          .join(", ")
      : "—";

  // Viewers / !instances.create must not open the wizard (deep link / sidebar).
  if (createAccessLoading || instancesLoading) {
    return <BaasInstancesSkeleton count={2} />;
  }
  if (!canCreateInstance) {
    return <Redirect to={BAAS_BASE_PATH} />;
  }

  return (
    <div className="baas-instance-create min-w-0 space-y-5">
      <PageHeaderWithBack
        title={intl.get("baas.instances.createPage.title")}
        subTitle={intl.get("baas.instances.createPage.subtitleFull")}
        backTo={BAAS_BASE_PATH}
      />

      <div className="space-y-2">
        <div
          className="flex gap-1.5 sm:gap-2"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-valuenow={stepIndex + 1}
          aria-label={intl.get("baas.instances.createPage.stepsNav")}
        >
          {steps.map((step, index) => {
            const done = index < stepIndex;
            const active = index === stepIndex;
            const clickable = done && !submitting;
            return (
              <button
                key={step.id}
                type="button"
                disabled={!clickable}
                onClick={() => goToStep(index)}
                className={`h-1.5 flex-1 rounded-full transition ${
                  done
                    ? "bg-emerald-500"
                    : active
                      ? "bg-[#e7a73f]"
                      : "bg-gray-200 dark:bg-brand-dark-border"
                } ${clickable ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                aria-label={intl.get(step.labelKey)}
                aria-current={active ? "step" : undefined}
              />
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {intl.get(currentStep.labelKey)} ({stepIndex + 1}/{steps.length})
          </p>
          <nav
            aria-label={intl.get("baas.instances.createPage.stepsNav")}
            className="hidden flex-wrap gap-1 md:flex"
          >
            {steps.map((step, index) => {
              const active = index === stepIndex;
              const done = index < stepIndex;
              return (
                <button
                  key={step.id}
                  type="button"
                  disabled={submitting || (!done && !active)}
                  onClick={() => (done ? goToStep(index) : undefined)}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                    active
                      ? "bg-[#e7a73f]/15 text-[#b87a1f] dark:text-[#e7a73f]"
                      : done
                        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                        : "bg-transparent text-gray-400 dark:text-gray-500"
                  }`}
                >
                  {done ? <TbCheck className="h-3 w-3 shrink-0" aria-hidden /> : null}
                  {intl.get(step.labelKey)}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className={panelClass}>
          {currentStep.id === "basics" ? (
            <>
              <h2 className="dashboard-section-label">
                {intl.get("baas.instances.createPage.sectionBasics")}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createModal.nameLabel")}
                  </label>
                  <input
                    className={inputClass}
                    value={instanceName}
                    onChange={(event) => setInstanceName(event.target.value)}
                    placeholder={intl.get("baas.instances.createModal.namePlaceholder")}
                    required
                    disabled={submitting}
                    autoFocus
                  />
                </div>
                <ToggleRow
                  label={intl.get("baas.instances.createPage.isProdLabel")}
                  hint={intl.get("baas.instances.createPage.isProdHintCreateLocked")}
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
              </div>
              <p className="rounded-lg border border-amber-200/70 bg-amber-50/50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100">
                {intl.get("baas.instances.createPage.hmlAlwaysHint")}
              </p>
              {isValutAdmin ? (
                <ToggleRow
                  label={intl.get("baas.instances.createPage.cloneConfigLabel")}
                  hint={intl.get("baas.instances.createPage.cloneConfigHint")}
                  checked={cloneConfig}
                  onChange={setCloneConfig}
                  disabled={submitting}
                />
              ) : null}
              {requiresOwnerEntity ? (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.ownerEntityLabel")}
                  </label>
                  <select
                    className={inputClass}
                    value={ownerEntityId}
                    onChange={(event) => setOwnerEntityId(event.target.value)}
                    disabled={submitting || loadingOwnerEntities}
                    required
                  >
                    <option value="">
                      {loadingOwnerEntities
                        ? intl.get("baas.instances.createPage.ownerEntityLoading")
                        : intl.get("baas.instances.createPage.ownerEntityPlaceholder")}
                    </option>
                    {ownerEntityOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.ownerEntityHint")}
                  </p>
                </div>
              ) : null}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.definitionLabel")}
                </label>
                <textarea
                  className={`${inputClass} h-20 resize-y py-2`}
                  value={definition}
                  onChange={(event) => setDefinition(event.target.value)}
                  placeholder={intl.get("baas.instances.createPage.definitionPlaceholder")}
                  disabled={submitting}
                  maxLength={1000}
                  rows={3}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.definitionHint")}
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.programCodeLabel")}
                </label>
                <input
                  className={inputClass}
                  value={programCodeTouched ? programCode : programCodePreview}
                  onChange={(event) => {
                    setProgramCodeTouched(true);
                    setProgramCode(event.target.value);
                  }}
                  disabled={submitting}
                  maxLength={48}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.programCodeHint")}
                </p>
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.countriesLabel")}
                </p>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.countriesHint")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {BAAS_SUPPORTED_COUNTRY_OPTIONS.map((item) => {
                    const checked = supportedCountries.includes(item.code);
                    return (
                      <label
                        key={item.code}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                          checked
                            ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                            : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#e7a73f]"
                          checked={checked}
                          onChange={() => toggleCountry(item.code)}
                          disabled={submitting}
                        />
                        <span>
                          {intl.get(`baas.instances.createPage.country.${item.code}`)} ({item.currency})
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <ToggleRow
                label={intl.get("baas.instances.createPage.allowKycLabel")}
                hint={intl.get("baas.instances.createPage.allowKycHint")}
                checked={allowKycApproval}
                onChange={setAllowKycApproval}
                disabled={submitting}
              />
              {!canCreate && !instancesLoading ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                  <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-100">
                    {intl.get("baas.instances.createPage.errorParentRequired")}
                  </p>
                </div>
              ) : null}
              {!isValutAdmin && canCreate ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40">
                  <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                    {intl.get("baas.instances.createPage.clientDefaultsHint")}
                  </p>
                </div>
              ) : null}
            </>
          ) : null}

          {currentStep.id === "email" ? (
            <>
              <h2 className="dashboard-section-label">
                {intl.get("baas.instances.createPage.sectionEmail")}
              </h2>
              <ToggleRow
                label={intl.get("baas.instances.createModal.receiveEmailLabel")}
                hint={intl.get("baas.instances.createModal.receiveEmailForcedOffHint")}
                checked={false}
                onChange={() => {}}
                disabled
              />
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createModal.templateIdLabel")}
                </label>
                <input
                  className={inputClass}
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                  placeholder={intl.get("baas.instances.createModal.templateIdPlaceholder")}
                  disabled={submitting}
                  autoFocus
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createModal.ipLabel")}
                </label>
                <input
                  className={inputClass}
                  value={allowedIps}
                  onChange={(event) => setAllowedIps(event.target.value)}
                  placeholder={intl.get("baas.instances.createModal.ipPlaceholder")}
                  disabled={submitting}
                />
              </div>
            </>
          ) : null}

          {currentStep.id === "root" ? (
            <>
              <h2 className="dashboard-section-label">
                {intl.get("baas.instances.createPage.sectionRoot")}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isIssuerOrCardsPreset
                  ? intl.get("baas.instances.createPage.virtualRootHint")
                  : intl.get("baas.instances.createPage.rootHint")}
              </p>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createModal.rootAccountLabel")}
                </label>
                {canEditRootAccount ? (
                  <>
                    <select
                      className={inputClass}
                      value={rootAccountChoice}
                      onChange={(event) => setRootAccountChoice(event.target.value)}
                      disabled={submitting || loadingRoots || !parentInstance}
                      autoFocus
                    >
                      <option value={ROOT_VIRTUAL}>
                        {intl.get("baas.instances.createPage.virtualRootOption")}
                      </option>
                      {rootOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                      <option value={ROOT_CUSTOM}>
                        {intl.get("baas.instances.createModal.rootAccountCustom")}
                      </option>
                      <option value="">
                        {intl.get("baas.instances.createModal.rootAccountNone")}
                      </option>
                    </select>
                    {rootAccountChoice === ROOT_CUSTOM ? (
                      <input
                        className={`${inputClass} mt-2`}
                        value={rootAccountCustom}
                        onChange={(event) => setRootAccountCustom(event.target.value)}
                        placeholder={intl.get("baas.instances.createModal.rootAccountPlaceholder")}
                        disabled={submitting}
                      />
                    ) : null}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {rootAccountChoice === ROOT_VIRTUAL
                        ? intl.get("baas.instances.createPage.virtualRootAdminHint")
                        : intl.get("baas.instances.createPage.rootAccountAdminHint")}
                    </p>
                    {rootAccountChoice !== ROOT_VIRTUAL ? (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {intl.get("baas.instances.createModal.rootAccountMustBePerson")}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <input
                      className={inputClass}
                      value={
                        loadingRoots
                          ? intl.get("baas.instances.createPage.rootAccountLoading")
                          : rootAccountLabel
                      }
                      disabled
                      readOnly
                      autoFocus
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {intl.get("baas.instances.createPage.rootAccountInheritedHint")}
                    </p>
                  </>
                )}
              </div>
            </>
          ) : null}

          {currentStep.id === "smtp" ? (
            <>
              <h2 className="dashboard-section-label">
                {intl.get("baas.instances.createPage.sectionSmtp")}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.instances.createModal.registerSmtpHint")}
              </p>
              <ToggleRow
                label={intl.get("baas.instances.createModal.registerSmtpLabel")}
                hint={intl.get("baas.instances.createModal.registerSmtpHint")}
                checked={registerSmtp}
                onChange={setRegisterSmtp}
                disabled={submitting}
              />

              {registerSmtp ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["host", "smtpHost"],
                    ["port", "smtpPort"],
                    ["username", "smtpUsername"],
                    ["password", "smtpPassword"],
                    ["fromEmail", "smtpFromEmail"],
                    ["fromName", "smtpFromName"],
                  ].map(([field, labelKey]) => (
                    <div key={field}>
                      <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                        {intl.get(`baas.instances.createModal.${labelKey}`)}
                      </label>
                      <input
                        className={inputClass}
                        type={
                          field === "password" ? "password" : field === "fromEmail" ? "email" : "text"
                        }
                        value={smtp[field]}
                        onChange={(event) =>
                          setSmtp((prev) => ({ ...prev, [field]: event.target.value }))
                        }
                        disabled={submitting}
                        autoComplete={field === "password" ? "new-password" : undefined}
                        inputMode={field === "port" ? "numeric" : undefined}
                        required={registerSmtp && (field === "fromEmail" || field === "password")}
                        autoFocus={field === "host"}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}

          {currentStep.id === "scopes" ? (
            <>
              <div>
                <h2 className="dashboard-section-label mb-1">
                  {intl.get("baas.instances.createPage.sectionScopes")}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.scopesHint")}
                </p>
              </div>
              {loadingScopes ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.scopesLoading")}
                </p>
              ) : (
                <BaasInstanceScopesPicker
                  catalog={scopeCatalog}
                  selected={selectedScopes}
                  onChange={setSelectedScopes}
                  disabled={submitting}
                  onPresetEnabled={handleScopePresetEnabled}
                />
              )}
            </>
          ) : null}

          {currentStep.id === "permissions" ? (
            <>
              <div>
                <h2 className="dashboard-section-label mb-1">
                  {intl.get("baas.instances.createPage.sectionPermissions")}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.permissionsHint")}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {BAAS_INSTANCE_FEATURE_OPTIONS.map((item) => (
                  <ToggleRow
                    key={item.code}
                    label={(() => {
                      const key = `baas.instances.createPage.feature.${item.code}`;
                      const value = intl.get(key);
                      return value && value !== key ? value : item.code;
                    })()}
                    hint={(() => {
                      const key = `baas.instances.createPage.featureHint.${item.code}`;
                      const value = intl.get(key);
                      return value && value !== key ? value : "";
                    })()}
                    checked={Boolean(features[item.code])}
                    onChange={(enabled) => {
                      setFeatures((prev) => ({ ...prev, [item.code]: enabled }));
                      if (item.code === "cards" && enabled) {
                        setRootAccountChoice((current) =>
                          current && current !== "" ? current : ROOT_VIRTUAL,
                        );
                        setSelectedScopes((current) =>
                          withCardsCoreScopes(current, scopeCatalog),
                        );
                      }
                    }}
                    disabled={submitting}
                  />
                ))}
              </div>
              {features.cards ? (
                <p className="rounded-lg border border-[#e7a73f]/40 bg-[#e7a73f]/10 px-3 py-2 text-xs text-brand-dark dark:text-gray-100">
                  {intl.get("baas.instances.createPage.cardsIncludes")}
                </p>
              ) : null}
            </>
          ) : null}

          {currentStep.id === "review" ? (
            <>
              <div>
                <h2 className="dashboard-section-label mb-1">
                  {intl.get("baas.instances.createPage.sectionReview")}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.reviewHint")}
                </p>
              </div>
              <dl className="rounded-xl border border-gray-200 px-3 dark:border-brand-dark-border sm:px-4">
                <ReviewRow
                  label={intl.get("baas.instances.createPage.isProdLabel")}
                  value={yesNo(isProduction)}
                />
                {isValutAdmin ? (
                  <ReviewRow
                    label={intl.get("baas.instances.createPage.cloneConfigLabel")}
                    value={yesNo(cloneConfig)}
                  />
                ) : null}
                <ReviewRow
                  label={intl.get("baas.instances.createPage.createHmlLabel")}
                  value={yesNo(true)}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createModal.nameLabel")}
                  value={instanceName.trim() || "—"}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.definitionLabel")}
                  value={definition.trim() || "—"}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.programCodeLabel")}
                  value={programCodePreview || "—"}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.countriesLabel")}
                  value={countriesReviewLabel}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.allowKycLabel")}
                  value={yesNo(allowKycApproval)}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createModal.receiveEmailLabel")}
                  value={yesNo(false)}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createModal.templateIdLabel")}
                  value={templateId.trim() || "—"}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createModal.ipLabel")}
                  value={allowedIps.trim() || "—"}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createModal.rootAccountLabel")}
                  value={rootAccountLabel}
                />
                <ReviewRow
                  label={intl.get("baas.instances.createModal.registerSmtpLabel")}
                  value={
                    registerSmtp
                      ? `${yesNo(true)}${smtp.fromEmail.trim() ? ` · ${smtp.fromEmail.trim()}` : ""}`
                      : yesNo(false)
                  }
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.sectionScopes")}
                  value={
                    isValutAdmin
                      ? intl.get("baas.instances.createPage.scopesSelected", {
                          count: selectedScopes.length,
                        })
                      : intl.get("baas.instances.createPage.reviewScopesDefault")
                  }
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.sectionPermissions")}
                  value={
                    enabledFeatures.length > 0
                      ? enabledFeatures
                          .map((item) => intl.get(`baas.instances.createPage.feature.${item.code}`))
                          .join(", ")
                      : "—"
                  }
                />
                <ReviewRow
                  label={intl.get("baas.instances.createPage.sectionLimits")}
                  value={intl.get("baas.instances.createPage.reviewLimitsDefault")}
                />
              </dl>
            </>
          ) : null}
        </div>

        {formError ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-brand-dark-border">
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2 !text-xs"
            onClick={handleBack}
            disabled={submitting}
          >
            <TbArrowLeft className="h-4 w-4" />
            {isFirstStep
              ? intl.get("baas.instances.createPage.cancel")
              : intl.get("baas.instances.createModal.back")}
          </button>

          {isLastStep ? (
            <button
              type="submit"
              className="btn-primary inline-flex items-center gap-2 !text-xs"
              disabled={submitting || !instanceName.trim() || !canCreate}
            >
              <TbCheck className="h-4 w-4" />
              {submitting
                ? intl.get("baas.instances.createPage.submitting")
                : intl.get("baas.instances.createModal.submit")}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2 !text-xs"
              onClick={handleNext}
              disabled={submitting || (isFirstStep && !instanceName.trim())}
            >
              {intl.get("baas.instances.createModal.next")}
              <TbChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export { BAAS_INSTANCE_CREATE_PATH };
